/**
 * Sprint 207P — Peer Invite + Support Trophy NFT (MVP-1)
 *
 * Lets a teen generate / fetch a personalised invite link and share it
 * with a friend. When the friend joins via the link and completes their
 * first challenge, the backend mints a "Support Trophy" NFT to this
 * teen's Vault (peer_invites.mint_peer_trophy_if_eligible).
 */
import React, { useEffect, useState } from 'react';
import { Users, Copy, Check, Share2, Trophy, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const PeerInviteCard = () => {
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.request('/api/peer-invites/create', { method: 'POST' });
        if (!cancelled) setInvite(data);
      } catch (e) {
        // Silent — card simply collapses if unauth or service is down.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCopy = async () => {
    if (!invite?.invite_url) return;
    try {
      await navigator.clipboard.writeText(invite.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {}
  };

  const handleShare = async () => {
    if (!invite?.invite_url) return;
    const text = `Practice Friends Forever 💚 — join me on HiBFF: ${invite.invite_url}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'HiBFF', text, url: invite.invite_url }); } catch (e) {}
    } else {
      try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) {}
    }
  };

  return (
    <div
      className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl p-5 border border-purple-100 shadow-sm"
      data-testid="peer-invite-card"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-md">
          <Users className="text-white" size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900">Invite a Friend</h3>
          <p className="text-xs text-gray-500">Earn a Support Trophy NFT 🏆 when they finish their first challenge</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 size={20} className="animate-spin text-purple-400" />
        </div>
      ) : !invite ? (
        <p className="text-sm text-gray-500 py-2">Sign in to generate your invite link.</p>
      ) : (
        <>
          <div
            className="bg-white rounded-lg border border-purple-200 px-3 py-2.5 flex items-center gap-2 mb-3"
            data-testid="peer-invite-link"
          >
            <code className="text-xs text-gray-700 flex-1 truncate font-mono">{invite.invite_url}</code>
            <button
              onClick={handleCopy}
              className={`p-1.5 rounded-md transition-colors ${
                copied ? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
              }`}
              data-testid="peer-invite-copy-btn"
              title="Copy link"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
          </div>

          <button
            onClick={handleShare}
            className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-lg font-semibold text-sm hover:shadow-md transition-shadow inline-flex items-center justify-center gap-2"
            data-testid="peer-invite-share-btn"
          >
            <Share2 size={16} />
            Share with a friend
          </button>

          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <span data-testid="peer-invite-stats-invitees">
              👥 {invite.invitee_count} joined
            </span>
            <span data-testid="peer-invite-stats-trophies" className="inline-flex items-center gap-1">
              <Trophy size={12} className="text-amber-500" /> {invite.trophy_count} trophies
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default PeerInviteCard;
